import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Backdrop,
  Badge,
  Popover,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  FontDownload as MenuIcon,
  Tune as FilterListIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors } from '../theme/theme';
import { STICKY_MENU_BREAKPOINTS } from './forms/shared/MediaQueryUtils';
import { ResetButton } from './common/Buttons';

interface AlphabeticalFilterProps {
  activeFilter: string | null;
  onFilterChange: (letter: string | null) => void;
  onTextFilterChange: (text: string) => void;
  textFilter: string;
  itemCount: number;
  contextType: 'clients' | 'pets' | 'bookings' | 'quotations';
  showMobileSearch: boolean;
  setShowMobileSearch: (show: boolean) => void;
  showMobileAlphabetMenu: boolean;
  setShowMobileAlphabetMenu: (show: boolean) => void;
  onMenuToggle?: (isOpen: boolean) => void;
  activeFilterCount?: number;
  onFilterButtonClick?: () => void;
  showAlphabetGrid?: boolean;
  showMobileAlphabetButton?: boolean;
  pageNumber?: number;
  pageSize?: number;
  totalCount?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onPageChange?: (newPage: number) => void;
  filterDescription?: string;
  onClearAllFilters?: () => void;
  onResetAll?: () => void;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const MobilePageTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'fixed',
  top: '4rem',
  left: 0,
  right: 0,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  padding: '0.5rem',
  fontSize: '1.5rem',
  fontWeight: 700,
  textTransform: 'capitalize',
  boxShadow: colors.shadows.medium,
  zIndex: 100,
  justifyContent: 'space-between',
  alignItems: 'center',

  [`@media (min-width: ${STICKY_MENU_BREAKPOINTS.tablet}px)`]: {
    display: 'none',
  },
}));

const MobileTitleLeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flex: 1,
});

const ActiveLetterBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.15)'
      : 'rgba(255, 255, 255, 0.25)',
  padding: '0.1rem 0.5rem',
  borderRadius: '4px',
  border: `1px solid ${theme.palette.secondary.contrastText}`,
  marginLeft: '1rem',
  fontWeight: 600,
}));

const ClearLetterButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
  padding: 0,
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
  },
}));

const MobileIconsContainer = styled(Box)({
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
});

const MobileIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
  padding: '8px',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(255, 255, 255, 0.2)',
  },
}));

const MobileSearchBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'fixed',
  top: '8rem',
  left: 0,
  right: 0,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.background.paper,
  padding: '0.3rem 1rem',
  zIndex: 99,
  boxShadow: colors.shadows.light,
  alignItems: 'center',
  gap: '0.5rem',
  borderBottom: `2px solid ${theme.palette.primary.light}`,

  [`@media (min-width: ${STICKY_MENU_BREAKPOINTS.tablet}px)`]: {
    display: 'none',
  },
}));

const MobileSearchTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '48px',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    '& fieldset': {
      borderColor: theme.palette.primary.light,
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '0 0.5rem',
    fontSize: '1.9rem',
    lineHeight: '48px',
    height: '48px',
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
  },
}));

const MobileAlphabetMenuContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '4rem',
  right: 0,
  width: '60px',
  maxHeight: 'calc(100vh - 4rem)',
  overflowY: 'auto',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? colors.primary.main
      : theme.palette.background.paper,
  boxShadow: colors.shadows.heavy,
  zIndex: 101,
  display: 'flex',
  flexDirection: 'column',
  border: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

const MobileLetterButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  minWidth: '60px',
  minHeight: '50px',
  padding: '0',
  margin: '0',
  borderRadius: '0',
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  borderLeft:
    theme.palette.mode === 'dark'
      ? `1px solid ${theme.palette.primary.main}`
      : 'none',

  fontSize: '2rem',
  fontWeight: 700,
  textTransform: 'uppercase',

  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.background.paper,
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[300]
      : theme.palette.tertiary.dark,

  '&:hover': {
    backgroundColor: active
      ? theme.palette.primary.dark
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.primary.light + '40',
    color: active
      ? theme.palette.primary.contrastText
      : theme.palette.mode === 'dark'
        ? colors.grey[0]
        : theme.palette.primary.dark,
  },
}));

const MobileAllButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  minWidth: '60px',
  minHeight: '66px',
  padding: '0',
  margin: '0',
  borderRadius: '0',
  borderBottom: `2px solid ${theme.palette.primary.light}`,
  fontSize: '1.5rem',
  fontWeight: 700,
  textTransform: 'uppercase',

  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[300]
      : theme.palette.tertiary.dark,

  '&:hover': {
    backgroundColor: active
      ? theme.palette.primary.dark
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.primary.light + '40',
  },
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? colors.primary.dark
      : theme.palette.grey[200],
  borderRadius: '0',
  padding: '0',
  margin: '0',
  width: '100%',
  boxSizing: 'border-box',
  boxShadow: colors.shadows.power,

  [`@media (min-width: ${STICKY_MENU_BREAKPOINTS.tablet}px)`]: {
    display: 'block',
  },
}));

const AlphabetGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  '& > *': {
    flex: '1 1 0',
  },
}));

const LetterButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  minWidth: 'unset',
  width: '100%',
  height: '60px',
  padding: '0',
  paddingBottom: active ? '5px' : 0,
  margin: '0',
  borderRadius: '0',
  border: 'none',
  borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark}`,
  borderBottom: `2px solid ${theme.palette.mode === 'dark' ? colors.primary.light : theme.palette.primary.dark}`,
  fontSize: active ? '3.2rem' : '1.9rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0',
  flex: '1 1 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: active ? 'default' : 'pointer',
  userSelect: 'none',
  backgroundColor: active
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.light
      : theme.palette.primary.main
    : theme.palette.mode === 'dark'
      ? theme.palette.tertiary.dark
      : theme.palette.tertiary.light,
  color: active
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : colors.text.light
    : theme.palette.mode === 'dark'
      ? colors.text.light
      : colors.primary.dark,
  '&:hover': {
    ...(!active && {
      fontSize: '3.2rem',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.primary.light
          : colors.primary.main,
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.primary.dark
          : colors.text.light,
      paddingBottom: '5px',
    }),
  },
  '&:last-child': {
    borderRight: 'none',
  },
}));

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.15rem 1rem',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : theme.palette.primary.main,
}));

const LeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  flex: '0 0 auto',
});

const RightSection = styled(Box)({
  flex: '0 0 auto',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '0.5rem',
});

const ViewingText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 'normal',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.light
      : theme.palette.primary.light,
  whiteSpace: 'nowrap',
}));

const CountText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 500,
  color:
    theme.palette.mode === 'dark' ? theme.palette.grey[50] : colors.text.light,
  whiteSpace: 'nowrap',
}));

const ActiveFiltersBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 0 .5rem 1rem',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : colors.primary.main,
  fontSize: '2rem',
  fontWeight: 500,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[0]
      : theme.palette.grey[0],
}));

const SearchPopoverTextField = styled(TextField)(({ theme }) => ({
  width: '300px',
  flexShrink: 0,
  '& .MuiOutlinedInput-root': {
    height: '48px',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    borderRadius: '4px 0 0 4px',
    '& fieldset': {
      borderColor: theme.palette.primary.light,
      borderWidth: '2px',
      borderRight: 'none',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
      borderRight: 'none',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      borderRight: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '2rem',
    fontWeight: 400,
    padding: '0 0.5rem',
    width: '100%',
    minWidth: '0',
    color: theme.palette.text.primary,
    '&::placeholder': {
      opacity: 0.6,
      fontWeight: '600',
    },
  },
}));

const SearchPopoverButton = styled(Button)(({ theme }) => ({
  minWidth: '136px',
  height: '40px',
  borderRadius: '0 2px 2px 0',
  fontSize: '1.5rem',
  fontWeight: 600,
  letterSpacing: 'normal',
  padding: '0 1rem',
  flexShrink: 0,
  boxShadow: 'none',
  transition: 'letter-spacing 0.2s ease',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.grey[0],

  '&:hover': {
    transform: 'none !important',
    boxShadow: 'none',
    letterSpacing: '2px',
    backgroundColor: theme.palette.primary.main,
  },

  '&:focus': {
    outline: `3px solid ${colors.accessibility.focusColor}`,
    outlineOffset: '2px',
  },
}));

const SearchPopoverButtonMobile = styled(Button)(({ theme }) => ({
  minWidth: '60px',
  height: '48px',
  borderRadius: '2px',
  fontSize: '1.5rem',
  fontWeight: 600,
  letterSpacing: 'normal',
  padding: '0 1rem',
  flexShrink: 0,
  boxShadow: 'none',
  transition: 'letter-spacing 0.2s ease',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.grey[0],

  '&:hover': {
    transform: 'none !important',
    boxShadow: 'none',
    letterSpacing: '2px',
    backgroundColor: theme.palette.primary.main,
  },

  '&:focus': {
    outline: `3px solid ${colors.accessibility.focusColor}`,
    outlineOffset: '2px',
  },
}));

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? colors.text.light : colors.text.light,
  '& svg': {
    fontSize: '2.5rem',
    transition: 'transform 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    '& svg': {
      transform: 'scale(1.2)',
    },
  },
}));

const AlphabeticalFilter: React.FC<AlphabeticalFilterProps> = ({
  activeFilter,
  onFilterChange,
  onTextFilterChange,
  textFilter,
  itemCount,
  contextType,
  showMobileSearch,
  setShowMobileSearch,
  showMobileAlphabetMenu,
  setShowMobileAlphabetMenu,
  onMenuToggle,
  activeFilterCount = 0,
  onFilterButtonClick,
  showAlphabetGrid = true,
  showMobileAlphabetButton = true,
  pageNumber = 1,
  pageSize = 100,
  totalCount = 0,
  hasNextPage = false,
  hasPreviousPage = false,
  onPageChange,
  filterDescription,
  onClearAllFilters,
  onResetAll,
}) => {
  const mobileSearchFieldRef = useRef<any>(null);

  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const showSearchModal = Boolean(searchAnchorEl);
  const [modalSearchText, setModalSearchText] = useState('');
  const searchModalInputRef = useRef<any>(null);

  const [mobileSearchText, setMobileSearchText] = useState('');

  useEffect(() => {
    if (showMobileSearch) {
      setMobileSearchText(textFilter);
      if (mobileSearchFieldRef.current) {
        mobileSearchFieldRef.current.focus();
      }
    }
  }, [showMobileSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showSearchModal) {
      setModalSearchText(textFilter);
      const focusTimer = setTimeout(() => {
        if (searchModalInputRef.current) {
          searchModalInputRef.current.focus();
          if (searchModalInputRef.current.value) {
            searchModalInputRef.current.select();
          }
        }
      }, 50);
      return () => clearTimeout(focusTimer);
    }
  }, [showSearchModal, textFilter]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const fallback = document.querySelector(
          '[aria-label="Open search"]'
        ) as HTMLElement;
        if (fallback) setSearchAnchorEl(fallback);
      }

      if (e.key === 'Escape' && showSearchModal) {
        setSearchAnchorEl(null);
      }

      if (e.key === 'Escape' && showMobileSearch) {
        setShowMobileSearch(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearchModal, showMobileSearch, setShowMobileSearch]);

  const handleLetterClick = (letter: string) => {
    onFilterChange(letter);
    if (onMenuToggle) {
      onMenuToggle(false);
    }
    setShowMobileAlphabetMenu(false);
  };

  const handleAllClick = () => {
    if (onResetAll) {
      onResetAll();
    } else {
      onFilterChange(null);
      onTextFilterChange('');
    }
    setMobileSearchText('');
    if (onMenuToggle) {
      onMenuToggle(false);
    }
  };

  const handleClearActiveFilter = () => {
    onFilterChange(null);
  };

  const handleClearMobileTextFilter = () => {
    setMobileSearchText('');
    onTextFilterChange('');
    if (mobileSearchFieldRef.current) {
      mobileSearchFieldRef.current.focus();
    }
  };

  const handleMobileSearchSubmit = () => {
    onTextFilterChange(mobileSearchText);
    setShowMobileSearch(false);
  };

  const handleMobileSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleMobileSearchSubmit();
    }
  };

  const handleCloseSearchModal = () => {
    setSearchAnchorEl(null);
  };

  const handleSearchModalInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalSearchText(e.target.value);
  };

  const handleSearchSubmit = () => {
    onTextFilterChange(modalSearchText);
    setSearchAnchorEl(null);
  };

  const handleSearchModalKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleClearSearchModal = () => {
    setModalSearchText('');
    if (searchModalInputRef.current) {
      searchModalInputRef.current.focus();
    }
  };

  const getDisplayText = () => {
    const hasPageInfo =
      pageNumber !== undefined &&
      pageSize !== undefined &&
      totalCount !== undefined;

    if (textFilter) {
      if (hasPageInfo) {
        return (
          <>
            <strong>{totalCount}</strong> {contextType} found for "{textFilter}"
          </>
        );
      }
      return `${contextType} found for "${textFilter}"`;
    }

    if (hasPageInfo) {
      if (totalCount === 0) {
        return (
          <>
            <strong>0</strong>{' '}
            <Box component={'span'} sx={{ textTransform: 'capitalize' }}>
              {contextType}
            </Box>
            {activeFilter && (
              <>
                {' '}
                found for <strong>{activeFilter}</strong>
              </>
            )}
          </>
        );
      }
      const startItem = (pageNumber - 1) * pageSize + 1;
      const endItem = Math.min(pageNumber * pageSize, totalCount);
      return (
        <>
          <strong>{startItem}</strong> - <strong>{endItem}</strong> of{' '}
          <strong>{totalCount}</strong>{' '}
          <Box
            component={'span'}
            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
          >
            {contextType}
          </Box>
          {activeFilter && (
            <>
              {' '}
              for <strong>{activeFilter}</strong>
            </>
          )}
        </>
      );
    }
    return `${contextType} total`;
  };

  return (
    <Box
      sx={{
        paddingTop: showMobileSearch ? '7rem' : '4rem',
        [`@media (min-width: ${STICKY_MENU_BREAKPOINTS.tablet}px)`]: {
          paddingTop: 0,
          position: 'sticky',
          top: '64px',
          zIndex: 999,
          backgroundColor: 'transparent',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.6)'
              : '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <MobilePageTitle>
        <MobileTitleLeftSection>
          <Typography
            variant="h5"
            component="span"
            sx={{ fontWeight: 700, fontSize: '2rem', marginLeft: '0.5rem' }}
          >
            {contextType}
          </Typography>
          {activeFilter && showAlphabetGrid !== false && (
            <ActiveLetterBadge>
              <Typography
                component="span"
                sx={{ fontWeight: 600, fontSize: '1.6rem' }}
              >
                {activeFilter}
              </Typography>
              <ClearLetterButton
                onClick={handleClearActiveFilter}
                aria-label="Clear letter filter"
                size="small"
              >
                <CloseIcon />
              </ClearLetterButton>
            </ActiveLetterBadge>
          )}
        </MobileTitleLeftSection>
        <MobileIconsContainer>
          {onFilterButtonClick && (
            <MobileIconButton
              onClick={onFilterButtonClick}
              aria-label="Open filters"
            >
              <Badge badgeContent={activeFilterCount || 0} color="primary">
                <FilterListIcon />
              </Badge>
            </MobileIconButton>
          )}
          <MobileIconButton
            onClick={() => setShowMobileSearch(!showMobileSearch)}
            aria-label="Toggle search"
          >
            <SearchIcon />
          </MobileIconButton>
          {showMobileAlphabetButton !== false && (
            <MobileIconButton
              onClick={() => setShowMobileAlphabetMenu(!showMobileAlphabetMenu)}
              aria-label="Toggle alphabet menu"
            >
              <MenuIcon />
            </MobileIconButton>
          )}
        </MobileIconsContainer>
      </MobilePageTitle>

      {showMobileSearch && (
        <MobileSearchBlock>
          <MobileSearchTextField
            inputRef={mobileSearchFieldRef}
            variant="outlined"
            placeholder={`Search ${contextType}...`}
            value={mobileSearchText}
            onChange={(e) => setMobileSearchText(e.target.value)}
            onKeyDown={handleMobileSearchKeyDown}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: (theme) => theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
              endAdornment: mobileSearchText && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearMobileTextFilter}
                    size="small"
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <SearchPopoverButtonMobile
            onClick={handleMobileSearchSubmit}
            variant="contained"
            color="primary"
          >
            Go
          </SearchPopoverButtonMobile>
          <IconButton
            onClick={() => setShowMobileSearch(false)}
            aria-label="Close search"
          >
            <CloseIcon />
          </IconButton>
        </MobileSearchBlock>
      )}

      {showMobileAlphabetMenu && showMobileAlphabetButton !== false && (
        <>
          <Backdrop
            open={showMobileAlphabetMenu}
            onClick={() => setShowMobileAlphabetMenu(false)}
            sx={(theme) => ({
              zIndex: 100,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(0, 0, 0, 0.75)'
                  : 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(3px)',
            })}
          />
          <MobileAlphabetMenuContainer>
            <MobileAllButton
              active={activeFilter === null}
              onClick={handleAllClick}
              aria-label="Show all"
            >
              ALL
            </MobileAllButton>
            {ALPHABET.map((letter) => (
              <MobileLetterButton
                key={letter}
                active={activeFilter === letter}
                onClick={() => handleLetterClick(letter)}
                aria-label={`Filter by ${letter}`}
              >
                {letter}
              </MobileLetterButton>
            ))}
          </MobileAlphabetMenuContainer>
        </>
      )}

      <Popover
        open={showSearchModal}
        anchorEl={searchAnchorEl}
        onClose={handleCloseSearchModal}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
        sx={{
          [`@media (max-width: ${STICKY_MENU_BREAKPOINTS.tablet - 1}px)`]: {
            display: 'none',
          },
          '& .MuiPopover-paper': {
            mt: '4px',
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0.5rem' }}>
          <SearchPopoverTextField
            inputRef={searchModalInputRef}
            variant="outlined"
            placeholder={`Find a ${contextType.slice(0, -1)}...`}
            value={modalSearchText}
            onChange={handleSearchModalInputChange}
            onKeyDown={handleSearchModalKeyDown}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: '2rem',
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: modalSearchText && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearchModal}
                    size="small"
                    aria-label="Clear search"
                    edge="end"
                  >
                    <ClearIcon sx={{ fontSize: '2rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <SearchPopoverButton
            onClick={handleSearchSubmit}
            variant="contained"
            color="primary"
          >
            Search
          </SearchPopoverButton>
        </Box>
      </Popover>

      <FilterContainer>
        <FilterBar>
          <LeftSection>
            <ViewingText>Viewing</ViewingText>
            <CountText>{getDisplayText()}</CountText>

            {(activeFilter || textFilter) && (
              <ResetButton
                onClick={handleAllClick}
                aria-label={`Clear all filters and show all ${contextType}`}
              >
                <CloseIcon />
              </ResetButton>
            )}
          </LeftSection>

          <RightSection>
            {onFilterButtonClick && (
              <IconButton
                onClick={onFilterButtonClick}
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? colors.text.light
                      : colors.text.light,
                  '& svg': {
                    fontSize: '2.5rem',
                    transition: 'transform 0.2s ease',
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                    '& svg': {
                      transform: 'scale(1.25)',
                    },
                  },
                }}
                aria-label="Open filters"
              >
                <Badge badgeContent={activeFilterCount || 0} color="primary">
                  <FilterListIcon />
                </Badge>
              </IconButton>
            )}

            <SearchIconButton
              onClick={(e) => setSearchAnchorEl(e.currentTarget)}
              aria-label="Open search"
              title="Search (Ctrl+K / Cmd+K)"
            >
              <SearchIcon />
            </SearchIconButton>
          </RightSection>
        </FilterBar>

        {filterDescription && (
          <ActiveFiltersBar>
            <ViewingText>Filtered by&nbsp;</ViewingText> {filterDescription}
            {onClearAllFilters && (
              <ResetButton
                onClick={onClearAllFilters}
                aria-label="Clear all filters"
              >
                <CloseIcon />
              </ResetButton>
            )}
          </ActiveFiltersBar>
        )}

        {showAlphabetGrid !== false && (
          <AlphabetGrid>
            {ALPHABET.map((letter) => (
              <LetterButton
                key={letter}
                active={activeFilter === letter}
                onClick={() => handleLetterClick(letter)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    handleLetterClick(letter);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Filter ${contextType} by letter ${letter}`}
                aria-pressed={activeFilter === letter}
              >
                {letter}
              </LetterButton>
            ))}
          </AlphabetGrid>
        )}
      </FilterContainer>
    </Box>
  );
};

export default AlphabeticalFilter;
